// Email Type Split — donut chart showing Marketing vs Transactional volume.

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

const COLORS = {
  transactional: 'var(--color-brand-500)',
  marketing: 'var(--color-info)',
} as const

interface EmailTypeSplitProps {
  transactional: number
  marketing: number
}

export function EmailTypeSplit({ transactional, marketing }: EmailTypeSplitProps): React.JSX.Element {
  const total = transactional + marketing
  if (total === 0) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h3 className="text-sm font-medium text-text-primary">Email Type Split</h3>
        <p className="mt-4 text-center text-sm text-text-tertiary">No email data available.</p>
      </div>
    )
  }

  const data = [
    { name: 'Transactional', value: transactional },
    { name: 'Marketing', value: marketing },
  ]

  const txPct = ((transactional / total) * 100).toFixed(1)
  const mktPct = ((marketing / total) * 100).toFixed(1)

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-text-primary">Email Type Split</h3>
        <p className="text-xs text-text-tertiary">Transactional vs Marketing volume</p>
      </div>
      <div className="flex items-center gap-6">
        <ResponsiveContainer width="50%" height={200}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={40}
            >
              <Cell fill={COLORS.transactional} />
              <Cell fill={COLORS.marketing} />
            </Pie>
            <Tooltip
              formatter={(value: number) => value.toLocaleString()}
              contentStyle={{
                backgroundColor: 'var(--color-surface-0)',
                border: '1px solid var(--color-surface-3)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: COLORS.transactional }} />
              <span className="text-text-primary">Transactional</span>
            </div>
            <span className="font-medium text-text-secondary">
              {transactional.toLocaleString()} ({txPct}%)
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: COLORS.marketing }} />
              <span className="text-text-primary">Marketing</span>
            </div>
            <span className="font-medium text-text-secondary">
              {marketing.toLocaleString()} ({mktPct}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
